import {
  integrationsOverviewSchema,
  ldapIntegrationStatusSchema,
  mailIntegrationStatusSchema,
  okResponseSchema,
  webhookEndpointCreatedSchema,
  webhookEndpointSchema,
  webhookEndpointSecretsSchema,
  type UpdateLdapIntegrationRequest,
  type UpdateMailIntegrationRequest,
  type UpsertWebhookEndpointRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';

export const integrationsQueryKey = ['integrations'] as const;

const queuedSchema = z.object({ queued: z.literal(true) });

export function useIntegrations() {
  return useQuery({
    queryKey: integrationsQueryKey,
    queryFn: () => apiFetch('/integrations', integrationsOverviewSchema),
  });
}

export function useUpdateMailIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMailIntegrationRequest) =>
      apiFetch('/integrations/mail', mailIntegrationStatusSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function usePollMailNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/integrations/mail/poll', queuedSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useUpdateLdapIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLdapIntegrationRequest) =>
      apiFetch('/integrations/ldap', ldapIntegrationStatusSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useSyncLdapNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/integrations/ldap/sync', queuedSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useSyncEntraNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/integrations/entra/sync', queuedSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertWebhookEndpointRequest) =>
      apiFetch('/integrations/webhooks', webhookEndpointCreatedSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertWebhookEndpointRequest }) =>
      apiFetch(`/integrations/webhooks/${id}`, webhookEndpointSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useRotateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/integrations/webhooks/${id}/rotate`, webhookEndpointSecretsSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/integrations/webhooks/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
    },
  });
}
